import { DataTable, TablePagination } from '@/components/ui/data-table'
import { cn } from '@/lib/utils'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import Card from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import BannerImage from 'app/frontend/base/components/BannerImage'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { CircleSlash2Icon, Loader2, LockKeyhole } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import EventParticipantStatusControl from './EventParticipantStatusControl'
import { participantColor } from '../utils/participantColor'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import EventParticipantStatusItems from '../../TeamEvent/components/EventParticipantStatusItems'

dayjs.extend(RelativeTime)

type EventListTableProps = {
  userId?: number
  events: wowaudit.EventReference[]
  page: number
  totalPages: number
  isLoadingEvents: boolean
  isUpdatingStatus: boolean
  updatingEventId?: number
  onPageChange: (nextPage: number) => void
  onRowClick: (event: wowaudit.EventReference) => void
  membershipForEvent: (
    event: wowaudit.EventReference,
  ) => wowaudit.TeamMembership | undefined
  selectableStatusesForEvent: (
    event: wowaudit.EventReference,
  ) => wowaudit.EventParticipantReference['status'][]
  onSelectStatus: (input: {
    event: wowaudit.EventReference
    participant?: wowaudit.EventParticipantReference
    membership?: wowaudit.TeamMembership
    nextStatus: wowaudit.EventParticipantReference['status']
  }) => void
  canAdminEditEvent?: (event: wowaudit.EventReference) => boolean
  allowParticipantSelfEdit?: boolean
  includeTeamColumn?: boolean
  isBulkUpdating?: boolean
  bulkSelectableStatuses?: wowaudit.EventParticipantReference['status'][]
  onBulkSelectStatus?: (
    eventIds: number[],
    nextStatus: wowaudit.EventParticipantReference['status'],
  ) => void
}

const EventListTable = ({
  userId,
  events,
  page,
  totalPages,
  isLoadingEvents,
  isUpdatingStatus,
  updatingEventId,
  onPageChange,
  onRowClick,
  membershipForEvent,
  selectableStatusesForEvent,
  onSelectStatus,
  canAdminEditEvent,
  allowParticipantSelfEdit = false,
  includeTeamColumn = false,
  isBulkUpdating = false,
  bulkSelectableStatuses = [],
  onBulkSelectStatus,
}: EventListTableProps) => {
  const {
    data: { user },
  } = usePageInfo()
  const [openDropdownEventId, setOpenDropdownEventId] = useState<number>()
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false)
  const wasBulkUpdating = useRef(false)
  const hasEvents = events.length > 0
  const selectedEventIds = useMemo(
    () => Object.keys(rowSelection).map((id) => Number(id)),
    [rowSelection],
  )
  const selectedCount = selectedEventIds.length
  const canBulkEdit =
    !!user && !!onBulkSelectStatus && bulkSelectableStatuses.length > 0
  const showBulkBar = canBulkEdit

  useEffect(() => {
    if (wasBulkUpdating.current && !isBulkUpdating) {
      setRowSelection({})
    }
    wasBulkUpdating.current = isBulkUpdating
  }, [isBulkUpdating])

  const columns = useMemo<ColumnDef<wowaudit.EventReference>[]>(
    () => [
      {
        id: 'title',
        header: 'Event',
        cell: ({ row }) => {
          const event = row.original
          return (
            <div className='flex items-center gap-2'>
              <EventAvatar
                eventReference={event}
                className='h-8 w-8 rounded-full'
              />
              <div className='min-w-0 leading-tight'>
                <div className={cn('truncate')}>{event.title}</div>
                <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                  {event.status === 'locked' ? (
                    <LockKeyhole className='h-3 w-3 text-warning-foreground' />
                  ) : null}
                  {event.status === 'cancelled' ? (
                    <CircleSlash2Icon className='h-3 w-3 text-destructive-foreground' />
                  ) : null}
                  <span className='capitalize'>{event.activityType}</span>
                  {event.primaryDifficulty && event.activityType === 'raids' ? (
                    <span>- {difficultiesLabels[event.primaryDifficulty]}</span>
                  ) : null}
                  {event.optional ? (
                    <span className='text-xs text-info-foreground'>
                      (optional)
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          )
        },
      },
      ...(includeTeamColumn
        ? [
            {
              id: 'team',
              header: 'Team',
              cell: ({
                row,
              }: {
                row: { original: wowaudit.EventReference }
              }) => {
                const event = row.original
                const teamReference = (event as wowaudit.UserEventReference)
                  .teamReference
                return teamReference ? (
                  <div className='flex items-center gap-2'>
                    <BannerImage
                      faction={teamReference.ownerReference.faction}
                      fileName={teamReference.ownerReference.bannerFilename}
                      size='custom'
                      className='w-8 h-8 min-w-8 min-h-8'
                    />
                    <div className='min-w-0 leading-tight'>
                      <div className='truncate'>
                        {teamReference.ownerReference.name}
                      </div>
                      <div className='text-xs text-muted-foreground truncate'>
                        {teamReference.name}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='text-muted-foreground italic'>Unknown</div>
                )
              },
            } as ColumnDef<wowaudit.EventReference>,
          ]
        : []),
      {
        id: 'date',
        header: 'Date',
        cell: ({ row }) => {
          const event = row.original
          return (
            <div className='leading-tight whitespace-nowrap'>
              <div>{dayjs(event.startsAt).fromNow()}</div>
              <div className='text-xs text-muted-foreground'>
                {dayjs(event.date).format('D MMM YYYY')}
              </div>
            </div>
          )
        },
      },
      {
        id: 'time',
        header: 'Time',
        cell: ({ row }) => {
          const event = row.original
          return (
            <div className='whitespace-nowrap'>
              {event.startTime} - {event.finishTime}
            </div>
          )
        },
      },
      {
        id: 'composition',
        header: 'Composition',
        cell: ({ row }) => {
          const event = row.original
          const presentParticipants = event.participantReferences.filter(
            (participant) =>
              presentParticipantStatuses.includes(participant.status),
          )
          const presentTanks = presentParticipants.filter(
            (participant) => participant.role === 'tank',
          ).length
          const presentHealers = presentParticipants.filter(
            (participant) => participant.role === 'heal',
          ).length
          const presentDps = presentParticipants.filter(
            (participant) =>
              participant.role === 'melee' || participant.role === 'ranged',
          ).length
          return (
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-1.5 w-10'>
                <CharacterRoleIcon role='tank' size='medium' />
                <span>{presentTanks}</span>
              </div>
              <div className='flex items-center gap-1.5 w-10'>
                <CharacterRoleIcon role='heal' size='medium' />
                <span>{presentHealers}</span>
              </div>
              <div className='flex items-center gap-1.5 w-12'>
                <CharacterRoleIcon role='ranged' size='medium' />
                <span>{presentDps}</span>
              </div>
              <div className='h-6 w-px bg-border' />
              <span className='flex items-center gap-1 font-normal text-muted-foreground text-xs'>
                <span
                  className={cn(
                    'font-semibold text-sm',
                    participantColor(event),
                  )}
                >
                  {presentParticipants.length}
                </span>
                <span>/</span>
                <span>{event.participantReferences.length}</span>
              </span>
            </div>
          )
        },
      },
      ...(user
        ? [
            {
              id: 'signup',
              header: 'My status',
              cell: ({ row }) => {
                const event = row.original
                const participant = event.participantReferences.find(
                  (eventParticipant) =>
                    eventParticipant.characterReference.userId === userId,
                )
                const membership = membershipForEvent(event)
                const currentStatus = participant?.status ?? 'unknown'
                const canCreateSignup = !!membership
                const canEditParticipant =
                  event.status !== 'locked' &&
                  ((canAdminEditEvent && canAdminEditEvent(event)) ||
                    canCreateSignup ||
                    (allowParticipantSelfEdit && !!participant))
                const canEditStatus =
                  canEditParticipant && (!!participant || canCreateSignup)
                return (
                  <Card
                    className={cn(
                      'p-0 max-w-20',
                      canEditStatus
                        ? 'hover:bg-card transition-colors'
                        : 'border-transparent bg-transparent hover:bg-transparent',
                    )}
                  >
                    <EventParticipantStatusControl
                      main={membership}
                      participant={participant}
                      status={currentStatus}
                      canEditParticipant={canEditStatus}
                      selectableStatuses={selectableStatusesForEvent(event)}
                      isUpdatingStatus={
                        isUpdatingStatus && updatingEventId === event.id
                      }
                      open={openDropdownEventId === event.id}
                      iconSize='small'
                      onOpenChange={(open) =>
                        setOpenDropdownEventId(open ? event.id : undefined)
                      }
                      onSelect={(nextStatus) =>
                        onSelectStatus({
                          event,
                          participant,
                          membership,
                          nextStatus,
                        })
                      }
                      triggerClassName='focus:outline-none w-full h-8 flex items-center justify-center'
                      stopPropagation
                    />
                  </Card>
                )
              },
            } as ColumnDef<wowaudit.EventReference>,
          ]
        : []),
    ],
    [
      canAdminEditEvent,
      allowParticipantSelfEdit,
      bulkSelectableStatuses,
      includeTeamColumn,
      isUpdatingStatus,
      membershipForEvent,
      onSelectStatus,
      openDropdownEventId,
      onBulkSelectStatus,
      selectableStatusesForEvent,
      user,
      userId,
    ],
  )

  return (
    <Card className='space-y-2 p-0'>
      {showBulkBar || totalPages > 1 ? (
        <div className='flex items-center justify-between px-2 pt-2 gap-2'>
          {showBulkBar ? (
            <div
              className={cn(
                'flex items-center gap-2 ml-2',
                selectedCount === 0
                  ? 'opacity-0'
                  : 'opacity-100 transition-opacity',
              )}
            >
              <span>
                {selectedCount} event{selectedCount !== 1 ? 's' : ''} selected
              </span>
              <DropdownMenu
                open={bulkDropdownOpen}
                onOpenChange={setBulkDropdownOpen}
              >
                <DropdownMenuTrigger
                  className='inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card-nested hover:bg-accent px-2 text-xs'
                  disabled={isBulkUpdating || selectedCount === 0}
                >
                  {isBulkUpdating ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : null}
                  {isBulkUpdating ? 'Updating...' : 'Change status'}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className='w-fit p-1'
                  side='bottom'
                  align='start'
                >
                  <EventParticipantStatusItems
                    currentStatus='unknown'
                    statuses={bulkSelectableStatuses}
                    onSelect={(nextStatus) => {
                      onBulkSelectStatus?.(selectedEventIds, nextStatus)
                      setBulkDropdownOpen(false)
                    }}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div />
          )}
          {totalPages > 1 ? (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              showPreviousNext={true}
              disabled={isLoadingEvents}
            />
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          'relative transition-[min-height] duration-200',
          isLoadingEvents && !hasEvents && 'min-h-[60rem]',
        )}
      >
        {isLoadingEvents && (
          <div className='absolute inset-0 z-20 flex items-center justify-center bg-card/60 pointer-events-auto cursor-wait' />
        )}
        <div
          className={cn(
            'transition-opacity duration-200',
            isLoadingEvents && !hasEvents
              ? 'opacity-0 pointer-events-none'
              : '',
          )}
        >
          <DataTable
            className='whitespace-nowrap'
            data={events}
            columns={columns}
            rowSelection={canBulkEdit ? rowSelection : undefined}
            onRowSelectionChange={canBulkEdit ? setRowSelection : undefined}
            getRowId={(event) => event.id.toString()}
            wrapperComponent={({ children }) => <div>{children}</div>}
            includeHeader={true}
            onRowClick={onRowClick}
            rowClassName={(event) =>
              cn(
                event.status === 'cancelled'
                  ? 'bg-destructive'
                  : event.optional
                  ? 'bg-info'
                  : '',
                event.status === 'cancelled'
                  ? 'hover:bg-destructive/80'
                  : event.optional
                  ? 'hover:bg-info/80'
                  : 'hover:bg-card-nested',
              )
            }
            emptyMessage={() =>
              isLoadingEvents ? 'Loading events...' : 'No events found'
            }
          />
        </div>
      </div>

      {totalPages > 1 ? (
        <div className='flex justify-end px-2 pb-2'>
          <TablePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            showPreviousNext={true}
            disabled={isLoadingEvents}
          />
        </div>
      ) : null}
    </Card>
  )
}

export default EventListTable
